import { useMemo } from 'react'
import { useMsal } from '@azure/msal-react'

type Claims = Record<string, unknown> | undefined

const getString = (claims: Claims, key: string): string | undefined =>
  typeof claims?.[key] === 'string' ? (claims[key] as string) : undefined

const getStringArray = (claims: Claims, key: string): string[] =>
  Array.isArray(claims?.[key]) ? (claims?.[key] as string[]) : []

// Tries common Entra External ID/B2C patterns for custom attributes
const getExtensionClaim = (claims: Claims, name: string): string | undefined => {
  if (!claims) return undefined

  // 1) direct match
  const direct = getString(claims, name)
  if (direct) return direct

  // 2) known variants: extension_<name>, extension_*_<name>, custom:<name>
  const keys = Object.keys(claims)
  const match = keys.find(
    k =>
      k === `extension_${name}` ||
      (k.startsWith('extension_') && k.endsWith(`_${name}`)) ||
      k === `custom:${name}`,
  )
  return match ? getString(claims, match) : undefined
}

/**
 * useClaims: convenient accessors for frequently used token claims.
 * - name
 * - preferred_username (fallbacks to email/emails/username if not present)
 * - activehid (custom attribute; supports extension_ prefixed and custom: prefixes)
 */
export const useClaims = () => {
  const { instance, accounts } = useMsal()
  const account = instance.getActiveAccount() || accounts[0]

  return useMemo(() => {
    const claims = (account?.idTokenClaims as Claims) || undefined

    const name = getString(claims, 'name') || account?.username

    // preferred_username fallbacks
    const preferredDirect = getString(claims, 'preferred_username')
    const emails = getStringArray(claims, 'emails')
    const emailClaim = getString(claims, 'email')
    const preferred_username =
      preferredDirect || emailClaim || emails[0] || account?.username

    // customerid: look through extension/custom patterns
    const customerid = getExtensionClaim(claims, 'customerid')

    return {
      account,
      claims,
      name,
      preferred_username,
      customerid,
    }
  }, [account])
}

export type UseClaimsReturn = ReturnType<typeof useClaims>
